import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../shared/api';
import { formatMoney } from '../shared/formatting';
import { Page, PageHeader, Card, StatCard, DataTable, type Col } from '../shared/ui';

/**
 * Modul 1.4 "Pregled" — replika strukture automatskog dnevnog izvjestaja
 * "[SEGMENTACIJA <poslovnica>] HR Dnevni izvjestaj" (Auro Domus Support),
 * s biranjem perioda, poslovnice, regije ili mreze total (Vedran, mail
 * UID 553, 11.09.2026 — zahtjev Tonci Siklic).
 *
 * Backend: App\Support\AdoModule1\OverviewPregled, GET /ado-m1/overview-pregled.
 */

type PeriodBlock = {
    label: string;
    range: { from: string; to: string };
    range_ly: { from: string; to: string };
    tx: number; tx_ly: number; tx_diff: number; tx_pct: number | null;
    amount: number; amount_ly: number; amount_diff: number; amount_pct: number | null;
    ruc: number; ruc_ly: number; ruc_diff: number; ruc_pct: number | null;
};

type SegmentBucket = {
    key?: string;
    label: string;
    tx: number; tx_ly: number; tx_pct: number | null;
    amount: number; amount_ly: number; amount_pct: number | null;
    ruc: number; ruc_ly: number; ruc_pct: number | null;
};

type PriceSeries = {
    available: boolean;
    unit: string;
    series: { date: string; label: string; close: number }[];
    change_pct: number | null;
    min: number | null;
    max: number | null;
};

type Payload = {
    available: boolean;
    generated_at?: string;
    filter: { scope: string; poslovnica_id: number | null; poslovnica_naziv: string | null; region: string | null; date_from: string; date_to: string };
    options: { poslovnice: { id: number; naziv: string; region: string | null }[]; regije: string[] };
    price_trend?: { gold: PriceSeries; silver: PriceSeries };
    periods?: { day: PeriodBlock; week: PeriodBlock; month: PeriodBlock; custom: PeriodBlock };
    amount_segments?: { range: { from: string; to: string }; range_ly: { from: string; to: string }; buckets: SegmentBucket[] };
    customer_segments?: { range: { from: string; to: string }; range_ly: { from: string; to: string }; buckets: SegmentBucket[] };
    note?: string;
};

function todayIso(): string {
    return new Date().toISOString().slice(0, 10);
}

function pctColor(v: number | null): string {
    if (v === null) return '#8a8f99';
    return v >= 0 ? '#1f7a36' : '#b14040';
}

function pctText(v: number | null): string {
    if (v === null) return '—';
    return `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
}

/** Linijski graf cijene (EUR/g) — puna SVG linija s tockama i min/max osjencenjem. */
function PriceLineChart({ series, color, unit, changePct, title }: {
    series: { date: string; label: string; close: number }[];
    color: string; unit: string; changePct: number | null; title: string;
}) {
    if (series.length < 2) return <div style={{ color: '#8a8f99', fontSize: 12.5, padding: '12px 0' }}>Nema dovoljno podataka za graf.</div>;
    const closes = series.map((s) => s.close);
    const min = Math.min(...closes);
    const max = Math.max(...closes);
    const span = Math.max(max - min, max * 0.002, 0.0001);
    const W = 520;
    const H = 150;
    const padL = 44; const padR = 10; const padT = 12; const padB = 22;
    const x = (i: number) => padL + (i / (series.length - 1)) * (W - padL - padR);
    const y = (v: number) => padT + (1 - (v - min) / span) * (H - padT - padB);
    const path = series.map((s, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(s.close).toFixed(1)}`).join(' ');
    const area = `${path} L ${x(series.length - 1).toFixed(1)} ${H - padB} L ${padL} ${H - padB} Z`;
    const last = series[series.length - 1];
    const first = series[0];
    // ~5 datumskih oznaka na osi
    const tickEvery = Math.max(1, Math.round(series.length / 5));
    return (
        <div style={{ flex: '1 1 300px', minWidth: 280 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
                <span style={{ fontWeight: 700, fontSize: 13 }}>{title}</span>
                <span style={{ fontSize: 12, color: '#8a8f99' }}>{unit}</span>
                <span style={{ marginLeft: 'auto', fontSize: 12.5, fontWeight: 700, color: pctColor(changePct) }}>{pctText(changePct)}</span>
            </div>
            <div style={{ fontSize: 11.5, color: '#8a8f99', marginBottom: 4 }}>
                {first.label} {first.close.toFixed(2)} → {last.label} {last.close.toFixed(2)} · min {min.toFixed(2)} · max {max.toFixed(2)}
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }} role="img">
                <line x1={padL} y1={y(min)} x2={W - padR} y2={y(min)} stroke="#eceff3" strokeWidth={1} />
                <line x1={padL} y1={y(max)} x2={W - padR} y2={y(max)} stroke="#eceff3" strokeWidth={1} />
                <text x={padL - 6} y={y(max) + 4} fontSize={10} fill="#8a8f99" textAnchor="end">{max.toFixed(2)}</text>
                <text x={padL - 6} y={y(min) + 4} fontSize={10} fill="#8a8f99" textAnchor="end">{min.toFixed(2)}</text>
                <path d={area} fill={color} opacity={0.08} />
                <path d={path} fill="none" stroke={color} strokeWidth={2} />
                <circle cx={x(series.length - 1)} cy={y(last.close)} r={3.5} fill={color} />
                {series.map((s, i) => (i % tickEvery === 0 || i === series.length - 1) ? (
                    <text key={s.date} x={x(i)} y={H - 6} fontSize={9.5} fill="#8a8f99" textAnchor="middle">{s.label}</text>
                ) : null)}
            </svg>
        </div>
    );
}

/** Grupirani stupci: tekuce razdoblje vs prosla godina, za dan/tjedan/mjesec/custom. */
function PeriodBars({ rows, valueOf, valueOfLy, fmt, label }: {
    rows: (PeriodBlock & { id: string })[];
    valueOf: (r: PeriodBlock) => number; valueOfLy: (r: PeriodBlock) => number;
    fmt: (n: number) => string; label: string;
}) {
    const max = Math.max(1, ...rows.flatMap((r) => [valueOf(r), valueOfLy(r)]));
    return (
        <div style={{ flex: '1 1 240px', minWidth: 220 }}>
            <div style={{ fontWeight: 700, fontSize: 12.5, marginBottom: 8 }}>{label}</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, height: 130 }}>
                {rows.map((r) => {
                    const v = valueOf(r); const vLy = valueOfLy(r);
                    return (
                        <div key={r.id} style={{ flex: '1 1 0', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%', minWidth: 0 }}
                            title={`${r.label}\nSada: ${fmt(v)}\nLY: ${fmt(vLy)}`}>
                            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: '100%', justifyContent: 'center' }}>
                                <div style={{ width: '38%', maxWidth: 30, height: `${(v / max) * 100}%`, background: '#4a5a6f', borderRadius: '4px 4px 0 0', minHeight: v > 0 ? 3 : 1 }} />
                                <div style={{ width: '38%', maxWidth: 30, height: `${(vLy / max) * 100}%`, background: '#c2cbd6', borderRadius: '4px 4px 0 0', minHeight: vLy > 0 ? 3 : 1 }} />
                            </div>
                        </div>
                    );
                })}
            </div>
            <div style={{ display: 'flex', gap: 14, marginTop: 6 }}>
                {rows.map((r) => (
                    <div key={r.id} style={{ flex: '1 1 0', textAlign: 'center', fontSize: 10, color: '#8a8f99', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{shortPeriodLabel(r.id)}</div>
                ))}
            </div>
        </div>
    );
}

function shortPeriodLabel(id: string): string {
    if (id === 'day') return 'Dan';
    if (id === 'week') return 'Tjedan';
    if (id === 'month') return 'Mjesec';
    return 'Odabrano';
}

/** Vodoravne trake za segmente: iznos sada (puna) + LY (svjetlija ispod). */
function SegmentBars({ buckets, locale }: { buckets: SegmentBucket[]; locale: 'hr' | 'en' }) {
    const max = Math.max(1, ...buckets.flatMap((b) => [b.amount, b.amount_ly]));
    if (buckets.length === 0) return null;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 4, marginBottom: 12 }}>
            {buckets.map((b, i) => (
                <div key={b.key ?? `${b.label}-${i}`}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 3 }}>
                        <span style={{ fontSize: 12.5, fontWeight: 700, color: '#34383d', flex: 1 }}>{b.label}</span>
                        <span style={{ fontSize: 11, color: '#8a8f99' }}>{b.tx} TX</span>
                        <span style={{ fontSize: 12.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatMoney(b.amount, locale)}</span>
                        <span style={{ fontSize: 11.5, fontWeight: 700, color: pctColor(b.amount_pct), minWidth: 52, textAlign: 'right' }}>{pctText(b.amount_pct)}</span>
                    </div>
                    <div style={{ height: 9, background: '#eef2f6', borderRadius: 5, overflow: 'hidden' }}>
                        <div style={{ width: `${Math.max((b.amount / max) * 100, b.amount > 0 ? 1.5 : 0)}%`, height: '100%', background: '#4a5a6f', borderRadius: 5 }} />
                    </div>
                    <div style={{ height: 5, background: '#f4f6f9', borderRadius: 4, overflow: 'hidden', marginTop: 2 }} title={`LY: ${formatMoney(b.amount_ly, locale)}`}>
                        <div style={{ width: `${Math.max((b.amount_ly / max) * 100, b.amount_ly > 0 ? 1.5 : 0)}%`, height: '100%', background: '#c2cbd6', borderRadius: 4 }} />
                    </div>
                </div>
            ))}
            <div style={{ display: 'flex', gap: 14, fontSize: 11, color: '#8a8f99' }}>
                <span><span style={{ display: 'inline-block', width: 14, height: 7, background: '#4a5a6f', borderRadius: 3, marginRight: 5, verticalAlign: 'middle' }} />tekuće</span>
                <span><span style={{ display: 'inline-block', width: 14, height: 5, background: '#c2cbd6', borderRadius: 3, marginRight: 5, verticalAlign: 'middle' }} />prošla godina</span>
            </div>
        </div>
    );
}

export function AdoOverviewModule({ token, locale = 'hr' }: { token: string; locale?: 'hr' | 'en' }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);

    const [scope, setScope] = useState<'mreza' | 'poslovnica' | 'regija'>('mreza');
    const [poslovnicaId, setPoslovnicaId] = useState<number | ''>('');
    const [region, setRegion] = useState<string>('');
    const [dateFrom, setDateFrom] = useState<string>(todayIso());
    const [dateTo, setDateTo] = useState<string>(todayIso());

    const [data, setData] = useState<Payload | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        const qs = new URLSearchParams();
        qs.set('scope', scope);
        if (scope === 'poslovnica' && poslovnicaId !== '') qs.set('poslovnica_id', String(poslovnicaId));
        if (scope === 'regija' && region !== '') qs.set('region', region);
        qs.set('date_from', dateFrom);
        qs.set('date_to', dateTo);
        api<Payload>(`/ado-m1/overview-pregled?${qs.toString()}`, token)
            .then((res) => { if (!cancelled) setData(res); })
            .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Greška pri dohvatu podataka.'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [token, scope, poslovnicaId, region, dateFrom, dateTo]);

    const poslovnice = data?.options.poslovnice ?? [];
    const regije = data?.options.regije ?? [];

    const scopeLabel = useMemo(() => {
        if (!data) return '';
        if (data.filter.scope === 'poslovnica') return data.filter.poslovnica_naziv ?? t('poslovnica', 'branch');
        if (data.filter.scope === 'regija') return `${t('regija', 'region')}: ${data.filter.region ?? ''}`;
        return t('Mreža total', 'Network total');
    }, [data, locale]);

    const periodCols = (): Col<PeriodBlock & { id: string }>[] => [
        { key: 'label', label: t('Razdoblje', 'Period'), render: (r) => (
            <div>
                <div style={{ fontWeight: 700 }}>{r.label}</div>
                <div style={{ fontSize: '0.72rem', color: '#8a8f99' }}>{r.range.from} → {r.range.to}</div>
            </div>
        ) },
        { key: 'tx', label: t('Broj TX', 'TX count'), align: 'right', render: (r) => (
            <div>
                <div>{r.tx}</div>
                <div style={{ fontSize: '0.72rem', color: pctColor(r.tx_pct) }}>{r.tx_ly} LY · {pctText(r.tx_pct)}</div>
            </div>
        ) },
        { key: 'amount', label: t('Iznos s PDV', 'Amount incl. VAT'), align: 'right', render: (r) => (
            <div>
                <div>{formatMoney(r.amount, locale)}</div>
                <div style={{ fontSize: '0.72rem', color: pctColor(r.amount_pct) }}>{formatMoney(r.amount_ly, locale)} LY · {pctText(r.amount_pct)}</div>
            </div>
        ) },
        { key: 'ruc', label: 'RUC', align: 'right', render: (r) => (
            <div>
                <div>{formatMoney(r.ruc, locale)}</div>
                <div style={{ fontSize: '0.72rem', color: pctColor(r.ruc_pct) }}>{formatMoney(r.ruc_ly, locale)} LY · {pctText(r.ruc_pct)}</div>
            </div>
        ) },
    ];

    const segmentCols = (): Col<SegmentBucket & { id: string }>[] => [
        { key: 'label', label: t('Segment', 'Segment'), render: (r) => <span style={{ fontWeight: 700 }}>{r.label}</span> },
        { key: 'tx', label: 'TX', align: 'right', render: (r) => (
            <div>
                <div>{r.tx} <span style={{ color: '#8a8f99', fontWeight: 400 }}>({r.tx_ly} LY)</span></div>
                <div style={{ fontSize: '0.72rem', color: pctColor(r.tx_pct) }}>{pctText(r.tx_pct)}</div>
            </div>
        ) },
        { key: 'amount', label: t('Iznos', 'Amount'), align: 'right', render: (r) => (
            <div>
                <div>{formatMoney(r.amount, locale)}</div>
                <div style={{ fontSize: '0.72rem', color: pctColor(r.amount_pct) }}>{formatMoney(r.amount_ly, locale)} LY · {pctText(r.amount_pct)}</div>
            </div>
        ) },
        { key: 'ruc', label: 'RUC', align: 'right', render: (r) => (
            <div>
                <div>{formatMoney(r.ruc, locale)}</div>
                <div style={{ fontSize: '0.72rem', color: pctColor(r.ruc_pct) }}>{formatMoney(r.ruc_ly, locale)} LY · {pctText(r.ruc_pct)}</div>
            </div>
        ) },
    ];

    const periodRows = data?.periods
        ? [
            { id: 'day', ...data.periods.day },
            { id: 'week', ...data.periods.week },
            { id: 'month', ...data.periods.month },
            { id: 'custom', ...data.periods.custom },
        ]
        : [];

    const amountRows = (data?.amount_segments?.buckets ?? []).map((b, i) => ({ id: `a${i}`, ...b }));
    const customerRows = (data?.customer_segments?.buckets ?? []).map((b) => ({ id: b.key ?? b.label, ...b }));

    return (
        <Page>
            <PageHeader
                icon="🏬"
                title={`1.4 · ${t('Pregled', 'Overview')}`}
                subtitle={t(
                    'Investicijsko zlato — dan/tjedan/mjesec vs prošla godina, po poslovnici, regiji ili mreži total. Segment po iznosu i po tipu kupca.',
                    'Investment gold — day/week/month vs last year, by branch, region or network total. Segmented by amount and customer type.',
                )}
            />

            <Card pad={14}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-end' }}>
                    <div>
                        <div style={{ fontSize: '0.72rem', color: '#8a8f99', marginBottom: 4 }}>{t('Prikaz', 'Scope')}</div>
                        <select value={scope} onChange={(e) => setScope(e.target.value as typeof scope)} style={selectStyle}>
                            <option value="mreza">{t('Mreža total', 'Network total')}</option>
                            <option value="poslovnica">{t('Pojedinačna poslovnica', 'Single branch')}</option>
                            <option value="regija">{t('Regija', 'Region')}</option>
                        </select>
                    </div>
                    {scope === 'poslovnica' ? (
                        <div>
                            <div style={{ fontSize: '0.72rem', color: '#8a8f99', marginBottom: 4 }}>{t('Poslovnica', 'Branch')}</div>
                            <select value={poslovnicaId} onChange={(e) => setPoslovnicaId(e.target.value ? Number(e.target.value) : '')} style={selectStyle}>
                                <option value="">{t('— odaberi —', '— select —')}</option>
                                {poslovnice.map((p) => (
                                    <option key={p.id} value={p.id}>{p.naziv}</option>
                                ))}
                            </select>
                        </div>
                    ) : null}
                    {scope === 'regija' ? (
                        <div>
                            <div style={{ fontSize: '0.72rem', color: '#8a8f99', marginBottom: 4 }}>{t('Regija', 'Region')}</div>
                            <select value={region} onChange={(e) => setRegion(e.target.value)} style={selectStyle}>
                                <option value="">{t('— odaberi —', '— select —')}</option>
                                {regije.map((r) => (
                                    <option key={r} value={r}>{r}</option>
                                ))}
                            </select>
                        </div>
                    ) : null}
                    <div>
                        <div style={{ fontSize: '0.72rem', color: '#8a8f99', marginBottom: 4 }}>{t('Odabrano razdoblje od', 'Custom period from')}</div>
                        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={selectStyle} />
                    </div>
                    <div>
                        <div style={{ fontSize: '0.72rem', color: '#8a8f99', marginBottom: 4 }}>{t('do', 'to')}</div>
                        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={selectStyle} />
                    </div>
                </div>
            </Card>

            {error ? <Card pad={16} style={{ color: '#b14040' }}>{error}</Card> : null}
            {!error && data && !data.available ? (
                <Card pad={16} style={{ color: '#8a8f99' }}>{data.note ?? t('Podaci nisu dostupni.', 'Data not available.')}</Card>
            ) : null}

            {data && data.available ? (
                <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
                        <StatCard icon="🏬" color="#34383d" label={t('Prikaz', 'Scope')} value={scopeLabel || '—'} />
                        <StatCard icon="📅" color="#5c6b7f" label={t('Referentni datum', 'Reference date')} value={data.filter.date_to} />
                        <StatCard icon="🧾" color="#4a5a6f" label={t('TX u razdoblju', 'TX in period')} value={String(data.periods?.custom.tx ?? 0)} sub={`${data.periods?.custom.tx_ly ?? 0} LY · ${pctText(data.periods?.custom.tx_pct ?? null)}`} subColor={pctColor(data.periods?.custom.tx_pct ?? null)} />
                        <StatCard icon="💶" color="#8a6d3b" label={t('Iznos u razdoblju', 'Amount in period')} value={formatMoney(data.periods?.custom.amount ?? 0, locale)} sub={`${formatMoney(data.periods?.custom.amount_ly ?? 0, locale)} LY · ${pctText(data.periods?.custom.amount_pct ?? null)}`} subColor={pctColor(data.periods?.custom.amount_pct ?? null)} />
                    </div>

                    {(data.price_trend?.gold?.available || data.price_trend?.silver?.available) ? (
                        <Card pad={14}>
                            <div style={{ fontWeight: 700, marginBottom: 10 }}>{t('Trend cijene — zadnjih 30 dana', 'Price trend — last 30 days')}</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
                                {data.price_trend?.gold?.available ? (
                                    <PriceLineChart title={t('Zlato', 'Gold')} unit={data.price_trend.gold.unit} color="#8a6d3b"
                                        series={data.price_trend.gold.series} changePct={data.price_trend.gold.change_pct} />
                                ) : null}
                                {data.price_trend?.silver?.available ? (
                                    <PriceLineChart title={t('Srebro', 'Silver')} unit={data.price_trend.silver.unit} color="#7a7d82"
                                        series={data.price_trend.silver.series} changePct={data.price_trend.silver.change_pct} />
                                ) : null}
                            </div>
                        </Card>
                    ) : null}

                    <Card pad={14}>
                        <div style={{ fontWeight: 700, marginBottom: 8 }}>{t('A) Investicijsko — dan / tjedan / mjesec vs prošla godina', 'A) Investment — day / week / month vs last year')}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, marginBottom: 14 }}>
                            <PeriodBars rows={periodRows} label={t('Broj TX (tamno = sada, svijetlo = LY)', 'TX count (dark = now, light = LY)')}
                                valueOf={(r) => r.tx} valueOfLy={(r) => r.tx_ly} fmt={(n) => String(n)} />
                            <PeriodBars rows={periodRows} label={t('Iznos s PDV (tamno = sada, svijetlo = LY)', 'Amount incl. VAT (dark = now, light = LY)')}
                                valueOf={(r) => r.amount} valueOfLy={(r) => r.amount_ly} fmt={(n) => formatMoney(n, locale)} />
                            <PeriodBars rows={periodRows} label={t('RUC (tamno = sada, svijetlo = LY)', 'Margin (dark = now, light = LY)')}
                                valueOf={(r) => Math.max(r.ruc, 0)} valueOfLy={(r) => Math.max(r.ruc_ly, 0)} fmt={(n) => formatMoney(n, locale)} />
                        </div>
                        <DataTable cols={periodCols()} rows={periodRows} loading={loading} rowKey={(r) => r.id} />
                    </Card>

                    <Card pad={14}>
                        <div style={{ fontWeight: 700, marginBottom: 8 }}>{t('Segment (iznos stavke)', 'Segment (item amount)')}</div>
                        <div style={{ fontSize: '0.78rem', color: '#8a8f99', marginBottom: 8 }}>
                            {data.amount_segments?.range.from} → {data.amount_segments?.range.to} {t('vs isto razdoblje prošle godine', 'vs same period last year')}
                        </div>
                        <SegmentBars buckets={data.amount_segments?.buckets ?? []} locale={locale} />
                        <DataTable cols={segmentCols()} rows={amountRows} loading={loading} rowKey={(r) => r.id} />
                    </Card>

                    <Card pad={14}>
                        <div style={{ fontWeight: 700, marginBottom: 8 }}>{t('Segment kupca (broj kupnji)', 'Customer segment (purchase count)')}</div>
                        <div style={{ fontSize: '0.78rem', color: '#8a8f99', marginBottom: 8 }}>
                            {t('NOVI = prva kupnja ikad · RIJETKI = 2.–3. kupnja · POVREMENI = 4.–10. · STALNI = 11.+ (mrežno, cijela povijest)', 'NEW = first-ever purchase · RARE = 2nd–3rd · OCCASIONAL = 4th–10th · REGULAR = 11th+ (network-wide, full history)')}
                        </div>
                        <SegmentBars buckets={data.customer_segments?.buckets ?? []} locale={locale} />
                        <DataTable cols={segmentCols()} rows={customerRows} loading={loading} rowKey={(r) => r.id} />
                    </Card>

                    {data.note ? <div style={{ fontSize: '0.74rem', color: '#8a8f99' }}>{data.note}</div> : null}
                </>
            ) : null}
        </Page>
    );
}

const selectStyle: React.CSSProperties = {
    padding: '6px 10px',
    borderRadius: 8,
    border: '1px solid #d8dbe0',
    fontSize: '0.86rem',
    minWidth: 160,
};
